import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, QueryResult } from '@apollo/client';
import { toast } from 'react-toastify';
import { ADD_PROJECT } from '../../../graphql/ADD_PROJECT';
import { GET_PROJECT } from '../../../graphql/GET_PROJECT';
import { GET_ALL_PROJECTS } from '../../../graphql/GET_ALL_PROJECTS';
import { IClientUser } from '../../../interfaces/user';
import { saveAIPoolToSession } from '../../../utils/generatedImageCache';
import ProjectIdeaStep from './Steps/ProjectIdeaStep';
import ProjectDetailsStep from './Steps/ProjectDetailsStep';
import ImageUploadModal from './Steps/ImageUploadModal';
import ProjectPreviewStep from './Steps/ProjectPreviewStep';
import ProjectSuccessModal from './Steps/ProjectSuccessModal';
import { AddProjectCollaborationInput } from '../../../../types/globalTypes';

export interface Media {
    id: string;
    uploadType: string;
    fileType: string;
    fileUrl: string;
    isAiGeneratedJustNow?: boolean;
}

export interface GeneratedImagePool {
    messageId: string;
    images: Media[];
    selectedIds: string[];
    generatedAt: number;
}

export interface ProjectFormValues {
    title: string;
    description: string;
    type: string;
    shareWith: string;
    requiredSkills: string[];
    country: string;
    locations: string[];
    onlyCreatorsBasedInLocation: boolean;
    genres: string[];
    videoUrl?: string;
    medias: Media[];
    collaborations: AddProjectCollaborationInput[];
    fundingStatus?: string;
    progress?: string;
    imageMode?: 'ai' | 'manual' | null;
    aiGeneratedPool?: GeneratedImagePool;
}

interface ProjectFlowProps extends IClientUser { }

const ProjectFlow: React.FC<ProjectFlowProps> = (props) => {
    const history = useHistory();
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [projectIdea, setProjectIdea] = useState('');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [usedAI, setUsedAI] = useState(false);
    const [acceptedFeeTerms, setAcceptedFeeTerms] = useState(false);

    const [formValues, setFormValues] = useState<ProjectFormValues>({
        title: '',
        description: '',
        type: '',
        shareWith: '',
        requiredSkills: [],
        country: '',
        locations: [],
        onlyCreatorsBasedInLocation: false,
        genres: [],
        videoUrl: '',
        medias: [],
        collaborations: [],
        fundingStatus: '',
        progress: 'IDEATION',
        imageMode: null,
        aiGeneratedPool: undefined
    });

    const [showImageModal, setShowImageModal] = useState(false);

    const addProjectCompleted = async (data: any) => {
        const projectId = data.addProject.id;
        setCreatedProjectId(projectId);

        if (formValues.aiGeneratedPool) {
            saveAIPoolToSession(projectId, formValues.aiGeneratedPool);
        }

        setShowSuccessModal(true);
    };

    const handleViewProject = () => {
        if (createdProjectId) {
            history.push(`/project/${createdProjectId}`);
        }
    };

    const [addProject, { loading: isSubmitting }] = useMutation<QueryResult>(
        ADD_PROJECT,
        {
            onCompleted: addProjectCompleted,
            refetchQueries: [GET_PROJECT, { query: GET_ALL_PROJECTS }]
        }
    );

    const handleStep1Complete = (
        idea: string,
        useAI: boolean,
        aiGeneratedData?: Partial<ProjectFormValues>
    ) => {
        setProjectIdea(idea);
        setUsedAI(useAI);

        if (useAI && aiGeneratedData) {
            setFormValues((prev) => ({
                ...prev,
                ...aiGeneratedData
            }));
        }

        setCurrentStep(2);
    };

    const handleStep2Complete = (values: ProjectFormValues) => {
        setFormValues(prev => ({
            ...prev,
            ...values
        }));
        setShowImageModal(true);
    };

    const handleImageModalClose = () => {
        setShowImageModal(false);
    };

    const handleImagesComplete = (
        medias: Media[],
        mode: 'ai' | 'manual',
        aiPool?: GeneratedImagePool
    ) => {
        setFormValues(prev => ({
            ...prev,
            medias,
            imageMode: mode,
            aiGeneratedPool: aiPool
        }));
        setShowImageModal(false);
        setCurrentStep(3);
    };

    const handleEditStep = (step: number) => {
        setCurrentStep(step);
    };

    const handleSubmitProject = async () => {
        try {
            const isPublic = formValues.shareWith === 'PUBLIC';
            const mediaIds = formValues.medias.map((m) => Number(m.id));
            await addProject({
                variables: {
                    title: formValues.title,
                    description: formValues.description,
                    videoUrl: formValues.videoUrl ?? null,
                    type: formValues.type,
                    progress: formValues.progress ?? 'IDEATION',
                    isPublic,
                    medias: mediaIds,
                    collaborations: formValues.collaborations.filter((collab) =>
                        collab.professions && collab.professions.length > 0 && collab.professions[0].trim() !== ''
                    ),
                    fundingStatus: formValues.fundingStatus ?? '',
                    genres: formValues.genres,
                    locations: formValues.country ? [formValues.country] : [],
                    acceptedFeeTerms: props.user?.userType === 'BRAND' ? acceptedFeeTerms : false
                }
            });
        } catch (error) {
            toast.error('Failed to create project. Please try again.');
        }
    };

    return (
        <div className="new-project-wizard">
            {currentStep === 1 && (
                <ProjectIdeaStep onComplete={handleStep1Complete} />
            )}

            {currentStep === 2 && (
                <ProjectDetailsStep
                    initialValues={formValues}
                    onComplete={handleStep2Complete}
                    onBack={() => setCurrentStep(1)}
                />
            )}

            {showImageModal && (
                <ImageUploadModal
                    isOpen={showImageModal}
                    onClose={handleImageModalClose}
                    onComplete={handleImagesComplete}
                    projectIdea={projectIdea}
                    existingMode={formValues.imageMode}
                    existingMedias={formValues.medias}
                    existingAIPool={formValues.aiGeneratedPool}
                />
            )}

            {currentStep === 3 && (
                <ProjectPreviewStep
                    formValues={formValues}
                    onEdit={handleEditStep}
                    onSubmit={handleSubmitProject}
                    isSubmitting={isSubmitting}
                    user={props.user}
                    acceptedFeeTerms={acceptedFeeTerms}
                    setAcceptedFeeTerms={setAcceptedFeeTerms}
                />
            )}

            {showSuccessModal && createdProjectId && (
                <ProjectSuccessModal
                    isOpen={showSuccessModal}
                    onViewProject={handleViewProject}
                />
            )}
        </div>
    );
};

export default ProjectFlow;
